import React, { useState, useEffect } from 'react';
import { getApplicantStats } from '../utils/applicants';
import { getTeamStats } from '../utils/teams';
import { getProjectStats } from '../utils/projects';
import AdminLayout from '../layouts/AdminLayout';
import { FiUsers, FiLayers, FiFolder, FiTrendingUp, FiDownload, FiPieChart, FiBarChart2 } from 'react-icons/fi';

const Stats = () => {
  const [stats, setStats] = useState({
    applicants: null,
    teams: null,
    projects: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      setLoading(true);
      setError('');

      const [applicantsResult, teamsResult, projectsResult] = await Promise.all([
        getApplicantStats(),
        getTeamStats(),
        getProjectStats()
      ]);

      if (applicantsResult.success && teamsResult.success && projectsResult.success) {
        setStats({
          applicants: applicantsResult.data,
          teams: teamsResult.data,
          projects: projectsResult.data
        });
      } else {
        setError('حدث خطأ في جلب الإحصائيات');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('حدث خطأ في جلب الإحصائيات');
    } finally {
      setLoading(false);
    }
  };

  const exportData = () => {
    const data = {
      applicants: stats.applicants,
      teams: stats.teams,
      projects: stats.projects,
      generatedAt: new Date().toISOString(),
      generatedBy: 'Qimma Hackathon Admin Panel'
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qimma-hackathon-stats-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow mx-auto mb-4"></div>
            <p className="text-yellow">جاري التحميل...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-dark-blue border-2 border-yellow p-8 rounded-2xl max-w-md">
            <h2 className="text-xl font-semibold mb-2 text-yellow">خطأ</h2>
            <p className="text-white/80 mb-4">{error}</p>
            <button
              onClick={fetchAllStats}
              className="px-6 py-2 bg-yellow text-[#05339c] rounded-lg hover:bg-yellow/80 transition-all duration-300 font-semibold"
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const getPercentage = (value, total) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-yellow mb-2">
            الإحصائيات <span className="text-white">التفصيلية</span>
          </h1>
          <p className="text-white/70">تحليل شامل لبيانات الهاكاثون</p>
        </div>
        <button
          onClick={exportData}
          className="flex items-center gap-2 px-6 py-3 bg-yellow text-[#05339c] rounded-xl hover:bg-yellow/80 transition-all duration-300 font-semibold"
        >
          <FiDownload size={20} />
          <span>تصدير البيانات</span>
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-yellow p-3 rounded-lg">
              <FiUsers size={28} className="text-[#05339c]" />
            </div>
            <FiTrendingUp className="text-yellow" size={20} />
          </div>
          <p className="text-4xl font-bold mb-2 text-yellow">{stats.applicants?.total || 0}</p>
          <p className="text-white/70">إجمالي المتقدمين</p>
        </div>

        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-yellow p-3 rounded-lg">
              <FiLayers size={28} className="text-[#05339c]" />
            </div>
            <FiTrendingUp className="text-yellow" size={20} />
          </div>
          <p className="text-4xl font-bold mb-2 text-yellow">{stats.teams?.total || 0}</p>
          <p className="text-white/70">إجمالي الفرق</p>
        </div>

        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-yellow p-3 rounded-lg">
              <FiFolder size={28} className="text-[#05339c]" />
            </div>
            <FiTrendingUp className="text-yellow" size={20} />
          </div>
          <p className="text-4xl font-bold mb-2 text-yellow">{stats.projects?.submitted || 0}</p>
          <p className="text-white/70">مشاريع مقدمة</p>
        </div>

        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-yellow p-3 rounded-lg">
              <FiBarChart2 size={28} className="text-[#05339c]" />
            </div>
            <FiTrendingUp className="text-yellow" size={20} />
          </div>
          <p className="text-4xl font-bold mb-2 text-yellow">
            {stats.applicants?.averageAge ? Math.round(stats.applicants.averageAge) : 0}
          </p>
          <p className="text-white/70">متوسط العمر (سنة)</p>
        </div>
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Applicants Statistics */}
        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-yellow p-3 rounded-lg">
              <FiUsers className="text-[#05339c]" size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-yellow">إحصائيات المتقدمين</h3>
              <p className="text-sm text-white/60">توزيع حالات التقديم</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Accepted */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-white font-medium">المقبولون</span>
                <div className="flex items-center gap-2">
                  <span className="text-white/50 text-sm">
                    {getPercentage(stats.applicants?.accepted, stats.applicants?.total)}%
                  </span>
                  <span className="font-bold text-green-400">
                    {stats.applicants?.accepted || 0}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div 
                  className="bg-green-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${getPercentage(stats.applicants?.accepted, stats.applicants?.total)}%` }}
                ></div>
              </div>
            </div>

            {/* Rejected */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-white font-medium">المرفوضون</span>
                <div className="flex items-center gap-2">
                  <span className="text-white/50 text-sm">
                    {getPercentage(stats.applicants?.rejected, stats.applicants?.total)}%
                  </span>
                  <span className="font-bold text-red-400">
                    {stats.applicants?.rejected || 0}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div 
                  className="bg-red-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${getPercentage(stats.applicants?.rejected, stats.applicants?.total)}%` }}
                ></div>
              </div>
            </div>

            {/* Pending */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-white font-medium">قيد المراجعة</span>
                <div className="flex items-center gap-2">
                  <span className="text-white/50 text-sm">
                    {getPercentage(stats.applicants?.pending, stats.applicants?.total)}%
                  </span>
                  <span className="font-bold text-yellow">
                    {stats.applicants?.pending || 0}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div 
                  className="bg-yellow h-3 rounded-full transition-all duration-500"
                  style={{ width: `${getPercentage(stats.applicants?.pending, stats.applicants?.total)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Field Distribution */}
          {stats.applicants?.byField && Object.keys(stats.applicants.byField).length > 0 && (
            <div className="mt-8 pt-6 border-t border-white/10">
              <h4 className="font-semibold text-yellow mb-4 flex items-center gap-2">
                <FiPieChart size={18} />
                التوزيع حسب المجال
              </h4>
              <div className="space-y-3">
                {Object.entries(stats.applicants.byField)
                  .sort(([, a], [, b]) => b - a)
                  .map(([field, count]) => (
                    <div key={field} className="flex items-center justify-between">
                      <span className="text-white/70 text-sm">{field}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 bg-white/10 rounded-full h-2">
                          <div 
                            className="bg-yellow h-2 rounded-full"
                            style={{ width: `${getPercentage(count, stats.applicants.total)}%` }}
                          ></div>
                        </div>
                        <span className="font-semibold text-white w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Gender Distribution */}
          {stats.applicants?.byGender && Object.keys(stats.applicants.byGender).length > 0 && (
            <div className="mt-8 pt-6 border-t border-white/10">
              <h4 className="font-semibold text-yellow mb-4">التوزيع حسب الجنس</h4>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(stats.applicants.byGender).map(([gender, count]) => (
                  <div key={gender} className="bg-blue rounded-xl p-4 text-center border-2 border-white/10">
                    <p className="text-2xl font-bold text-yellow">{count}</p>
                    <p className="text-sm text-white mt-1">{gender}</p>
                    <p className="text-xs text-white/50 mt-1">
                      {getPercentage(count, stats.applicants.total)}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Teams and Projects Statistics */}
        <div className="space-y-6">
          {/* Teams Statistics */}
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-yellow p-3 rounded-lg">
                <FiLayers className="text-[#05339c]" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-yellow">إحصائيات الفرق</h3>
                <p className="text-sm text-white/60">توزيع حالات الفرق</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Approved */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">المقبولة</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/50 text-sm">
                      {getPercentage(stats.teams?.approved, stats.teams?.total)}%
                    </span>
                    <span className="font-bold text-green-400">
                      {stats.teams?.approved || 0}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3">
                  <div 
                    className="bg-green-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${getPercentage(stats.teams?.approved, stats.teams?.total)}%` }}
                  ></div>
                </div>
              </div>

              {/* Rejected */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">المرفوضة</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/50 text-sm">
                      {getPercentage(stats.teams?.rejected, stats.teams?.total)}%
                    </span>
                    <span className="font-bold text-red-400">
                      {stats.teams?.rejected || 0}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3">
                  <div 
                    className="bg-red-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${getPercentage(stats.teams?.rejected, stats.teams?.total)}%` }}
                  ></div>
                </div>
              </div>

              {/* Pending */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">قيد المراجعة</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/50 text-sm">
                      {getPercentage(stats.teams?.pending, stats.teams?.total)}%
                    </span>
                    <span className="font-bold text-yellow">
                      {stats.teams?.pending || 0}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3">
                  <div 
                    className="bg-yellow h-3 rounded-full transition-all duration-500"
                    style={{ width: `${getPercentage(stats.teams?.pending, stats.teams?.total)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Projects Statistics */}
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-yellow p-3 rounded-lg">
                <FiFolder className="text-[#05339c]" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-yellow">إحصائيات المشاريع</h3>
                <p className="text-sm text-white/60">حالة تقديم المشاريع</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Submitted */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">المقدمة</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/50 text-sm">
                      {getPercentage(stats.projects?.submitted, stats.projects?.total)}%
                    </span>
                    <span className="font-bold text-green-400">
                      {stats.projects?.submitted || 0}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3">
                  <div 
                    className="bg-green-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${getPercentage(stats.projects?.submitted, stats.projects?.total)}%` }}
                  ></div>
                </div>
              </div>

              {/* Not Submitted */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">غير المقدمة</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/50 text-sm">
                      {getPercentage(stats.projects?.notSubmitted, stats.projects?.total)}%
                    </span>
                    <span className="font-bold text-white/70">
                      {stats.projects?.notSubmitted || 0}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3">
                  <div 
                    className="bg-white/50 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${getPercentage(stats.projects?.notSubmitted, stats.projects?.total)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Visual Stats */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="bg-blue rounded-xl p-4 text-center border-2 border-green-500">
                <p className="text-3xl font-bold text-green-400">{stats.projects?.submitted || 0}</p>
                <p className="text-sm text-white mt-1">مشاريع مكتملة</p>
              </div>
              <div className="bg-blue rounded-xl p-4 text-center border-2 border-white/20">
                <p className="text-3xl font-bold text-white/70">{stats.projects?.notSubmitted || 0}</p>
                <p className="text-sm text-white/70 mt-1">بانتظار التقديم</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="mt-8 bg-dark-blue border-2 border-yellow rounded-2xl p-8">
        <h3 className="text-2xl font-bold mb-6 text-center text-yellow">ملخص شامل</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <p className="text-4xl font-bold text-yellow">{stats.applicants?.total || 0}</p>
            <p className="text-white/70 text-sm mt-2">إجمالي المتقدمين</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-yellow">{stats.teams?.total || 0}</p>
            <p className="text-white/70 text-sm mt-2">إجمالي الفرق</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-yellow">{stats.applicants?.accepted || 0}</p>
            <p className="text-white/70 text-sm mt-2">مشاركين مقبولين</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-yellow">{stats.projects?.submitted || 0}</p>
            <p className="text-white/70 text-sm mt-2">مشاريع مقدمة</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Stats;
